/**
 * Random event definitions (spec sections 22, 36).
 *
 * Each event, while active, multiplies demand in its scope (a district or the
 * whole city) and may apply a one-time reputation change. Positive events lift
 * demand (concert/festival); negative events depress it (rain, outages).
 * Probabilities are per game-day. All values are data (tunable) — EventEngine
 * applies them.
 */
export type EventKind = 'positive' | 'negative';
export type EventScope = 'district' | 'city';

export interface EventDef {
  id: string;
  name: string;
  kind: EventKind;
  scope: EventScope;
  /** Eligible districts for a 'district'-scoped event (undefined = any MVP district). */
  districts?: string[];
  probabilityPerDay: number;
  minDurationHours: number;
  maxDurationHours: number;
  /** Demand multiplier while active (>1 lifts, <1 suppresses). */
  demandMultiplier: number;
  /** One-time reputation change applied to affected player lots at start. */
  reputationDelta?: number;
  /** Emoji + message template ({district} is substituted). */
  message: string;
}

export const EVENTS: EventDef[] = [
  // --- positive ---
  {
    id: 'concert',
    name: 'Concert',
    kind: 'positive',
    scope: 'district',
    districts: ['downtown', 'shopping'],
    probabilityPerDay: 0.06,
    minDurationHours: 3,
    maxDurationHours: 5,
    demandMultiplier: 3.5,
    message: '🎵 Concert in {district}! Parking demand is surging.',
  },
  {
    id: 'festival',
    name: 'Festival',
    kind: 'positive',
    scope: 'district',
    probabilityPerDay: 0.05,
    minDurationHours: 4,
    maxDurationHours: 8,
    demandMultiplier: 2.5,
    message: '🎉 Festival in {district} — lots of visitors!',
  },
  {
    id: 'shopping_campaign',
    name: 'Shopping Campaign',
    kind: 'positive',
    scope: 'district',
    districts: ['shopping'],
    probabilityPerDay: 0.06,
    minDurationHours: 6,
    maxDurationHours: 10,
    demandMultiplier: 2.0,
    message: '🛍️ Big sale in {district} — shoppers incoming!',
  },
  {
    id: 'office_convention',
    name: 'Office Convention',
    kind: 'positive',
    scope: 'district',
    districts: ['office', 'downtown'],
    probabilityPerDay: 0.05,
    minDurationHours: 4,
    maxDurationHours: 6,
    demandMultiplier: 2.2,
    message: '💼 Convention in {district} — extra demand.',
  },
  {
    id: 'football_match',
    name: 'Football Match',
    kind: 'positive',
    scope: 'district',
    districts: ['downtown'],
    probabilityPerDay: 0.04,
    minDurationHours: 2,
    maxDurationHours: 4,
    demandMultiplier: 3.0,
    message: '⚽ Match day in {district} — packed!',
  },

  // --- negative ---
  {
    id: 'heavy_rain',
    name: 'Heavy Rain',
    kind: 'negative',
    scope: 'city',
    probabilityPerDay: 0.07,
    minDurationHours: 2,
    maxDurationHours: 5,
    demandMultiplier: 0.6,
    message: '🌧️ Heavy rain city-wide — fewer drivers out.',
  },
  {
    id: 'power_failure',
    name: 'Power Failure',
    kind: 'negative',
    scope: 'district',
    probabilityPerDay: 0.04,
    minDurationHours: 1,
    maxDurationHours: 3,
    demandMultiplier: 0.1,
    message: '⚡ Power failure in {district} — barely operating.',
  },
  {
    id: 'system_outage',
    name: 'System Outage',
    kind: 'negative',
    scope: 'district',
    probabilityPerDay: 0.03,
    minDurationHours: 1,
    maxDurationHours: 2,
    demandMultiplier: 0.15,
    message: '🖥️ System outage in {district} — payments down.',
  },
  {
    id: 'barrier_failure',
    name: 'Barrier Failure',
    kind: 'negative',
    scope: 'district',
    probabilityPerDay: 0.05,
    minDurationHours: 1,
    maxDurationHours: 2,
    demandMultiplier: 0.5,
    message: '🚧 Barrier failure in {district} — slow entry.',
  },
  {
    id: 'security_incident',
    name: 'Security Incident',
    kind: 'negative',
    scope: 'district',
    probabilityPerDay: 0.03,
    minDurationHours: 1,
    maxDurationHours: 1,
    demandMultiplier: 0.9,
    reputationDelta: -4,
    message: '🚨 Security incident in {district} — reputation hit.',
  },
];
